import React, { memo, useCallback } from 'react';

import { Box } from '@mui/material';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  fhaServiceTypeModeOptionsMap,
  fhaServiceTypeOptions,
  FieldTypesEnum,
} from 'constants/loanActionEditor';
import { deleteByIndex, setIn } from 'utils/data';

import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import { wrapperClassname } from './LoanActionItem.constants';
import { DynamicValueInput } from './LoanActionItems';

const LoanActionFhaServiceRow = ({
  index,
  rootPath,
  isDisabled = false,
  isEditAllowed,
  isSingleItem,
}) => {
  const { form, setInForm, setValidationErrors } = useFormContext();

  const fieldPath = `${rootPath}[${index}]`;
  const serviceTypeFieldPath = `${fieldPath}.serviceType`;
  const modeFieldPath = `${fieldPath}.mode`;

  const forms = _.get(form, rootPath);
  const serviceType = _.get(form, serviceTypeFieldPath);
  const modeOptions = fhaServiceTypeModeOptionsMap[serviceType?.value];

  const onDeleteRow = useCallback(() => {
    const newForms = [...forms];

    newForms.splice(index, 1);

    setInForm(rootPath, newForms);
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), index)),
    );
  }, [setInForm, setValidationErrors, index, forms, rootPath]);

  const onChangeServiceType = useCallback(() => {
    setInForm(modeFieldPath, null);
  }, [setInForm, modeFieldPath]);

  return (
    <Box className={wrapperClassname}>
      <Box className="flex">
        <DynamicValueInput
          required
          className="grow"
          disabled={isDisabled || !isEditAllowed}
          label="FHA Service Type"
          name={serviceTypeFieldPath}
          options={fhaServiceTypeOptions}
          type={FieldTypesEnum.SELECT}
          onChange={onChangeServiceType}
        />
        {isEditAllowed && !isSingleItem && (
          <ButtonWithTooltip
            data-testid={loanManagementTestIds.getDeleteRowButtonId(fieldPath)}
            disabled={isDisabled}
            icon={DeleteIcon}
            size="medium"
            spanContainerClassName="flex items-center"
            title="Delete row"
            onClick={onDeleteRow}
          />
        )}
      </Box>

      {!!modeOptions && (
        <DynamicValueInput
          required
          disabled={isDisabled || !isEditAllowed}
          label="Mode"
          name={modeFieldPath}
          options={modeOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}
    </Box>
  );
};

LoanActionFhaServiceRow.propTypes = {
  isSingleItem: PropTypes.bool.isRequired,
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
};

export default memo(LoanActionFhaServiceRow);
